import {
    Card,
    Box,
    Badge,
    Typography,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Icon,
    IconButton,
    Tooltip,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Fab
} from "@mui/material";
import { format, parseISO, startOfWeek, addDays, addWeeks, isWeekend, differenceInDays } from "date-fns";
import { isHoliday } from "../../../utils/holidayUtils";
import {
    organizeGroupsByRemoteStatus,
    getDisconnectsForDate,
    getAvailableGroupsForPrint,
    filterDataForPrint,
    getFilteredGroupCount,
    generatePrintFilename,
    generatePrintContent,
    executePrint,
    filterQualifyingAccounts,
    PRINT_STYLES
} from "../../../utils/printUtils";
import React from "react";
import { useState, useRef, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import PrintIcon from "@mui/icons-material/Print";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getWeekly, markDisconnectAsPending, resendDisconnectNotice, revertAccountDisconnect, rescheduleAccountDisconnect, markDisconnectsAsPendingBulk, rescheduleAccountDisconnectsBulk, resendDisconnectNoticesBulk, revertAccountDisconnectsBulk } from "../../accountDisconnects/api/actions";
import { post as createDisconnectHold, postBulk as createDisconnectHoldsBulk } from "../../disconnectHolds/api/actions";
import { setSnackbar } from "../../snackbar/api/snackbar";
import { CalendarTodayOutlined, EmailOutlined, PauseOutlined, PlayArrowOutlined, ReplayOutlined, EventOutlined, WeekendOutlined, Refresh, ClearOutlined, ChevronLeft, ChevronRight } from "@mui/icons-material";
import PrintDialog from "./PrintDialog";
import { getScheduleDatePart, getDisconnectScheduleDateStr } from "../utils/dateUtils";

const WeekView = ({ Data: propData, accountDisconnects, getWeekly, markDisconnectAsPending, resendDisconnectNotice, revertAccountDisconnect, rescheduleAccountDisconnect, createDisconnectHold, markDisconnectsAsPendingBulk, rescheduleAccountDisconnectsBulk, resendDisconnectNoticesBulk, revertAccountDisconnectsBulk, createDisconnectHoldsBulk }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [selectedDate, setSelectedDate] = useState(null);
    const [printDialogOpen, setPrintDialogOpen] = useState(false);
    const [holdModalOpen, setHoldModalOpen] = useState(false);
    const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
    const [revertModalOpen, setRevertModalOpen] = useState(false);
    const [holdNote, setHoldNote] = useState("");
    const [selectedAccount, setSelectedAccount] = useState(null);
    // Track expanded accordions to keep them open
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [expandedProperties, setExpandedProperties] = useState(new Set());
    const cardRefs = useRef({});
    const [isRunLoading, setIsRunLoading] = useState(false);
    const [isHoldLoading, setIsHoldLoading] = useState(false);
    const [isRescheduleLoading, setIsRescheduleLoading] = useState(false);
    const [isResendLoading, setIsResendLoading] = useState(false);
    const [isRevertLoading, setIsRevertLoading] = useState(false);

    // Track optimistically updated disconnects (marked as pending locally)
    const [pendingDisconnects, setPendingDisconnects] = useState(new Set());
    
    // Multiselect state
    const [selectedDisconnects, setSelectedDisconnects] = useState(new Set());
    const [bulkHoldDialogOpen, setBulkHoldDialogOpen] = useState(false);
    const [bulkRunNowDialogOpen, setBulkRunNowDialogOpen] = useState(false);
    const [bulkRescheduleDialogOpen, setBulkRescheduleDialogOpen] = useState(false);
    const [bulkResendNoticeDialogOpen, setBulkResendNoticeDialogOpen] = useState(false);
    const [bulkRevertDialogOpen, setBulkRevertDialogOpen] = useState(false);
    const [bulkActionResultsOpen, setBulkActionResultsOpen] = useState(false);
    const [bulkActionResults, setBulkActionResults] = useState(null);
    const [bulkActionType, setBulkActionType] = useState(null);
    const [disconnectUuidToName, setDisconnectUuidToName] = useState(new Map());
    const [disconnectUuidToId, setDisconnectUuidToId] = useState(new Map());

    // 0 = week containing today; ±1 = adjacent weeks
    const [weekOffset, setWeekOffset] = useState(0);

    // Displayed week start (Monday) — must stay in sync with getWeekly weekStartDate
    const getWeekStartDate = () => {
        const today = new Date();
        const monday = startOfWeek(today, { weekStartsOn: 1 });
        return addWeeks(monday, weekOffset);
    };

    const getWeekStartDateString = () => format(getWeekStartDate(), 'yyyy-MM-dd');

    useEffect(() => {
        setSelectedDate(null);
    }, [weekOffset]);

    useEffect(() => {
        const weekStartDate = getWeekStartDateString();
        getWeekly(weekStartDate, '');
    }, [weekOffset, getWeekly]);

    const displayedWeekEnd = addDays(getWeekStartDate(), 6);

    const handleRefresh = () => {
        const weekStartDate = getWeekStartDateString();
        
        getWeekly(weekStartDate, '')
            .then(() => {
                // On successful refresh, clear optimistic updates as data is now fresh
                setPendingDisconnects(new Set());
                // Clear selections on refresh
                setSelectedDisconnects(new Set());
                dispatch(setSnackbar(true, "success", "Data refreshed"));
            })
            .catch((error) => {
                console.error("Refresh error:", error);
                dispatch(setSnackbar(true, "error", "Failed to refresh data. Please try again."));
            });
    };

    // Helper function to refresh data after actions (silently, without snackbar)
    const refreshData = () => {
        const weekStartDate = getWeekStartDateString();
        getWeekly(weekStartDate, '')
            .then(() => {
                // On successful refresh, clear optimistic updates as data is now fresh
                setPendingDisconnects(new Set());
                // Clear selections on refresh
                setSelectedDisconnects(new Set());
            })
            .catch((error) => {
                console.error("Background refresh error:", error);
                // Keep optimistic updates in place since refresh failed
            });
    };


    // Helper functions to determine current and previous stage
    // Uses notices.mostRecentNotice from API which returns: '14-day', '10-day', 'final', or 'disconnect'
    const getCurrentStage = (account) => {
        if (!account) return null;

        const mostRecentNotice = account.notices?.mostRecentNotice;
        if (!mostRecentNotice) return null;

        // Map API values to stage names
        const stageMap = {
            'disconnect': 'Disconnected',
            'final': 'Final Notice',
            '10-day': '10-day',
            '14-day': '14-day'
        };

        return stageMap[mostRecentNotice] || null;
    };

    const getPreviousStage = (account) => {
        if (!account) return null;

        const mostRecentNotice = account.notices?.mostRecentNotice;
        if (!mostRecentNotice) return null;

        // Map current stage to previous stage for revert functionality
        // Flow: 14-day → 10-day → Final → Disconnect
        if (mostRecentNotice === 'disconnect') {
            return 'Final';  // Disconnect → revert to Final
        } else if (mostRecentNotice === 'final') {
            // Final → revert to 10-day or 14-day (check which letter exists)
            // Check 10-day first since customers can get both letters, and 10-day is more recent
            const notices = account.notices || {};
            if (notices.letter10Day) {
                return '10-day';
            } else if (notices.letter14Day) {
                return '14-day';
            }
            return null;
        } else if (mostRecentNotice === '10-day') {
            return '14-day';  // 10-day → revert to 14-day
        } else if (mostRecentNotice === '14-day') {
            return null;  // 14-day → cannot revert (earliest stage)
        }

        return null;
    };

    const getRunNowType = (account) => {
        const stage = getCurrentStage(account);
        if (stage === "14-day" || stage === "10-day") {
            return "final_ready";
        }
        return "";
    };

    const getRunNowLabel = (account) => {
        const stage = getCurrentStage(account);
        if (stage === "14-day" || stage === "10-day") {
            return "Generate Final Notice";
        }
        return "Process Disconnect";
    };

    const handleRunNow = async (account) => {
        const disconnectUuid = account.disconnectUuid;
        if (!disconnectUuid) {
            dispatch(setSnackbar(true, "error", "Disconnect UUID not found"));
            return;
        }

        const pendingType = getRunNowType(account);
        const actionLabel = getRunNowLabel(account);

        try {
            setIsRunLoading(true);
            setPendingDisconnects((prev) => new Set(prev).add(disconnectUuid));
            setSelectedDisconnects((prev) => {
                const next = new Set(prev);
                next.delete(disconnectUuid);
                return next;
            });
            dispatch(setSnackbar(true, "info", `${actionLabel} - marking as pending...`));
            await markDisconnectAsPending(disconnectUuid, null, false, pendingType);

            dispatch(setSnackbar(true, "success", `${actionLabel} - marked as pending successfully`));
            setIsRunLoading(false);
            
            refreshData();
        } catch (error) {
            console.error("Error marking disconnect as pending:", error);
            setIsRunLoading(false);
            setPendingDisconnects(prev => {
                const updated = new Set(prev);
                updated.delete(disconnectUuid);
                return updated;
            });
            const errorMessage = error?.response?.data?.ERROR ||
                error?.response?.data?.message ||
                error?.message ||
                "Failed to mark disconnect as pending";
            dispatch(setSnackbar(true, "error", errorMessage));
        }
    };

    const handleHold = (account) => {
        setSelectedAccount(account);
        setHoldModalOpen(true);
    };

    const handleHoldSubmit = async () => {
        if (!holdNote.trim()) return;

        if (!selectedAccount) {
            dispatch(setSnackbar(true, "error", "Account information not found"));
            return;
        }

        const accountId = selectedAccount.accountId;
        if (!accountId) {
            dispatch(setSnackbar(true, "error", "Account ID not found"));
            return;
        }

        try {
            setIsHoldLoading(true);
            dispatch(setSnackbar(true, "info", "Holding disconnection..."));
            // Format expiresAt as YYYY-MM-DD HH:mm:ss
            const expiresAtDate = new Date();
            expiresAtDate.setDate(expiresAtDate.getDate() + 30);
            const expiresAt = expiresAtDate.toISOString().slice(0, 19).replace('T', ' ');

            const holdData = {
                holdType: 'ACCOUNT',
                accountId: accountId,
                reason: holdNote.trim(),
                notes: holdNote.trim(),
                expiresAt: expiresAt,
                isActive: 1,
                liftReason: '',
            };

            await createDisconnectHold(holdData);

            dispatch(setSnackbar(true, "success", "Disconnection held successfully"));
            setIsHoldLoading(false);
            setHoldModalOpen(false);
            setHoldNote("");
            setSelectedAccount(null);
            
            // Refresh data to reflect the hold
            refreshData();

        } catch (error) {
            console.error("Error holding disconnect:", error);
            setIsHoldLoading(false);
            const errorMessage = error?.response?.data?.ERROR ||
                error?.response?.data?.message ||
                error?.message ||
                "Failed to hold disconnection";
            dispatch(setSnackbar(true, "error", errorMessage));
        }
    };

    const handleReschedule = (account) => {
        setSelectedAccount(account);
        setRescheduleModalOpen(true);
    };

    const handleRescheduleSubmit = async () => {
        if (!selectedAccount) {
            dispatch(setSnackbar(true, "error", "Account information not found"));
            return;
        }

        const disconnectUuid = selectedAccount.disconnectUuid || selectedAccount.accountDisconnectId || selectedAccount.uuid;
        if (!disconnectUuid) {
            dispatch(setSnackbar(true, "error", "Disconnect UUID not found"));
            return;
        }

        try {
            setIsRescheduleLoading(true);
            dispatch(setSnackbar(true, "info", "Rescheduling disconnection..."));
            await rescheduleAccountDisconnect(disconnectUuid);

            dispatch(setSnackbar(true, "success", "Disconnection rescheduled successfully"));
            setIsRescheduleLoading(false);
            setRescheduleModalOpen(false);
            setSelectedAccount(null);
            
            // Refresh data to reflect the reschedule
            refreshData();
        } catch (error) {
            console.error("Error rescheduling disconnect:", error);
            setIsRescheduleLoading(false);
            const errorMessage = error?.response?.data?.ERROR ||
                error?.response?.data?.message ||
                error?.message ||
                "Failed to reschedule disconnection";
            dispatch(setSnackbar(true, "error", errorMessage));
        }
    };

    const handleResendNotice = async (account) => {
        const disconnectUuid = account.disconnectUuid || account.accountDisconnectId || account.uuid;
        if (!disconnectUuid) {
            dispatch(setSnackbar(true, "error", "Disconnect UUID not found"));
            return;
        }

        try {
            setIsResendLoading(true);
            dispatch(setSnackbar(true, "info", "Resending notice..."));
            await resendDisconnectNotice(disconnectUuid);
            dispatch(setSnackbar(true, "success", "Notice resent successfully"));
            setIsResendLoading(false);
            
            // Refresh data to reflect the resent notice
            refreshData();
        } catch (error) {
            console.error("Error resending notice:", error);
            setIsResendLoading(false);
            const errorMessage = error?.response?.data?.ERROR ||
                error?.response?.data?.message ||
                error?.message ||
                "Failed to resend notice";
            dispatch(setSnackbar(true, "error", errorMessage));
        }
    };

    const handleRevert = (account) => {
        setSelectedAccount(account);
        const previousStage = getPreviousStage(account);
        if (!previousStage) {
            const currentStage = getCurrentStage(account);
            if (currentStage === "14-day") {
                dispatch(setSnackbar(true, "error", "Cannot revert - already at the earliest stage (14-day)"));
            } else {
                dispatch(setSnackbar(true, "error", `Cannot revert from ${currentStage || "unknown stage"}`));
            }
            return;
        }
        setRevertModalOpen(true);
    };

    const handleRevertSubmit = async () => {
        if (!selectedAccount) {
            dispatch(setSnackbar(true, "error", "Account information not found"));
            setRevertModalOpen(false);
            return;
        }

        const disconnectUuid = selectedAccount.disconnectUuid || selectedAccount.accountDisconnectId || selectedAccount.uuid;
        if (!disconnectUuid) {
            dispatch(setSnackbar(true, "error", "Disconnect UUID not found"));
            setRevertModalOpen(false);
            return;
        }

        // Determine previous stage
        const previousStage = getPreviousStage(selectedAccount);
        if (!previousStage) {
            const currentStage = getCurrentStage(selectedAccount);
            if (currentStage === "14-day") {
                dispatch(setSnackbar(true, "error", "Cannot revert - already at the earliest stage (14-day)"));
            } else {
                dispatch(setSnackbar(true, "error", `Cannot revert from ${currentStage || "unknown stage"}`));
            }
            setRevertModalOpen(false);
            return;
        }

        try {
            setIsRevertLoading(true);
            dispatch(setSnackbar(true, "info", "Reverting disconnect to previous stage..."));
            await revertAccountDisconnect(disconnectUuid, previousStage);
            dispatch(setSnackbar(true, "success", "Disconnect reverted to previous stage successfully"));
            setIsRevertLoading(false);
            setRevertModalOpen(false);
            setSelectedAccount(null);
            
            // Refresh data to reflect the revert
            refreshData();
        } catch (error) {
            console.error("Error reverting disconnect:", error);
            setIsRevertLoading(false);
            const errorMessage = error?.response?.data?.ERROR ||
                error?.response?.data?.message ||
                error?.message ||
                "Failed to revert disconnect";
            dispatch(setSnackbar(true, "error", errorMessage));
        }
    };

    // Helper function to get all accounts from current view
    const getAllAccountsFromView = () => {
        const allAccounts = [];
        if (!selectedDate || !dataToUse.data[selectedDate]) {
            return allAccounts;
        }
        
        const selectedDateData = dataToUse.data[selectedDate];
        const { remote, nonRemote } = organizeGroupsByRemoteStatus(selectedDateData);
        const targetDateStr = typeof selectedDateData.date === 'string' && selectedDateData.date.includes('T')
            ? selectedDateData.date.split('T')[0]
            : selectedDateData.date;
        
        [...remote, ...nonRemote].forEach((group) => {
            Object.values(group.properties || {}).forEach((property) => {
                const allAccountsForProperty = getDisconnectsForDate(property, targetDateStr);
                const accounts = filterQualifyingAccounts(allAccountsForProperty, targetDateStr);
                allAccounts.push(...accounts);
            });
        });
        
        return allAccounts;
    };

    // Selection handlers
    const handleSelectDisconnect = (disconnectUuid) => {
        if (!disconnectUuid || pendingDisconnects.has(disconnectUuid)) return;
        setSelectedDisconnects((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(disconnectUuid)) {
                newSet.delete(disconnectUuid);
            } else {
                newSet.add(disconnectUuid);
            }
            return newSet;
        });
    };

    const handleSelectAll = (event, accounts) => {
        if (event.target.checked) {
            const allUuids = new Set(
                accounts
                    .filter((account) => {
                        const uuid = account.disconnectUuid || account.accountDisconnectId || account.uuid;
                        return uuid && !pendingDisconnects.has(uuid) && account.batchPending !== 1;
                    })
                    .map((account) => account.disconnectUuid || account.accountDisconnectId || account.uuid)
            );
            setSelectedDisconnects(allUuids);
        } else {
            setSelectedDisconnects(new Set());
        }
    };

    // Helper function to get full name
    const getFullName = (account) => {
        const hasName = account.firstName || account.lastName;
        return hasName
            ? `${account.firstName || ''} ${account.lastName || ''}`.trim()
            : (account.pmName || 'N/A');
    };

    // Bulk action handlers
    const handleHoldMulti = () => {
        if (selectedDisconnects.size === 0) return;
        setHoldNote("");
        setBulkHoldDialogOpen(true);
    };

    const handleRunNowMulti = () => {
        if (selectedDisconnects.size === 0) return;
        setBulkRunNowDialogOpen(true);
    };

    const handleRescheduleMulti = () => {
        if (selectedDisconnects.size === 0) return;
        setBulkRescheduleDialogOpen(true);
    };

    const handleResendNoticeMulti = () => {
        if (selectedDisconnects.size === 0) return;
        setBulkResendNoticeDialogOpen(true);
    };

    const handleRevertMulti = () => {
        if (selectedDisconnects.size === 0) return;
        setBulkRevertDialogOpen(true);
    };

    const apiData = propData || accountDisconnects?.weeklyData;

    // Generate all 7 days of the week (Monday through Sunday)
    const generateWeekDays = () => {
        const weekStart = getWeekStartDate();
        const weekDays = {};

        // Create a lookup map from API data by date (YYYY-MM-DD)
        // New structure: apiData has date strings as keys directly
        const apiDataByDate = {};
        if (apiData) {
            // Handle both old structure (apiData.data) and new structure (apiData with date keys)
            const dataSource = apiData.data || apiData;
            Object.entries(dataSource).forEach(([dateKey, dayData]) => {
                if (dayData?.date) {
                    // Extract date string - could be "2025-12-15" or ISO string
                    const dateStr = typeof dayData.date === 'string' && dayData.date.includes('T')
                        ? dayData.date.split('T')[0]
                        : dayData.date;
                    apiDataByDate[dateStr] = dayData;
                } else if (dayData && !dayData.date) {
                    // If the key itself is the date string
                    const dateStr = dateKey.split('T')[0];
                    apiDataByDate[dateStr] = { ...dayData, date: dateStr };
                }
            });
        }

        // Generate all 7 days with consistent key format
        for (let i = 0; i < 7; i++) {
            const currentDay = addDays(weekStart, i);
            const isoDate = format(currentDay, 'yyyy-MM-dd');

            // Create date key using the ISO date to avoid timezone issues
            const dateForKey = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate(), 0, 0, 0, 0);
            const dateKey = dateForKey.toString();

            // Use API data if available, otherwise create empty structure with schedules
            const apiDayData = apiDataByDate[isoDate];

            weekDays[dateKey] = apiDayData || {
                date: isoDate,
                schedules: {}
            };
        }

        return {
            data: weekDays,
            pagination: apiData?.pagination || {}
        };
    };

    const dataToUse = generateWeekDays();

    // Safety check: ensure data exists
    if (!dataToUse || !dataToUse.data) {
        if (accountDisconnects?.weeklyIsLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 4 }}>
                    <Typography>Loading...</Typography>
                </Box>
            );
        }
        if (accountDisconnects?.weeklyError) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 4 }}>
                    <Typography color="error">
                        Error loading data: {accountDisconnects.weeklyError.message || 'Unknown error'}
                    </Typography>
                </Box>
            );
        }
        if (!apiData) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 4 }}>
                    <Typography color="text.secondary">
                        No data loaded. Please try refreshing the page.
                    </Typography>
                </Box>
            );
        }
    }

    const handleSetSelectedDate = (date) => {
        setSelectedDate(date);
        // Clear selections when date changes
        setSelectedDisconnects(new Set());
    }

    useEffect(() => {
        if (selectedDate && cardRefs.current[selectedDate]) {
            setTimeout(() => {
                const cardElement = cardRefs.current[selectedDate];
                if (cardElement) {
                    cardElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                        inline: 'nearest'
                    });
                }
            }, 200);
        }
    }, [selectedDate]);

    // filterQualifyingAccounts is imported from printUtils to keep UI and print in sync

    // Function to count accounts for a given date
    const getDisconnectCountForDate = (dayData) => {
        if (!dayData) return 0;

        const targetDateStr = typeof dayData.date === 'string' && dayData.date.includes('T')
            ? dayData.date.split('T')[0]
            : dayData.date;

        const { remote, nonRemote } = organizeGroupsByRemoteStatus(dayData);
        let count = 0;

        [...remote, ...nonRemote].forEach((group) => {
            count += getGroupStats(group, targetDateStr).itemCount;
        });

        return count;
    }

    // Get selected date data
    const getSelectedDateData = () => {
        if (!selectedDate || !dataToUse.data[selectedDate]) {
            return null;
        }
        return dataToUse.data[selectedDate];
    }


    // Count properties and items for a group (aggregated from schedules)
    const getGroupStats = (propertyGroup, targetDateStr) => {
        if (!propertyGroup || !propertyGroup.properties) {
            return { propertyCount: 0, itemCount: 0 };
        }

        const properties = Object.values(propertyGroup.properties);
        let itemCount = 0;
        let propertyCount = 0;

        properties.forEach((property) => {
            const allAccounts = getDisconnectsForDate(property, targetDateStr);
            const qualifyingAccounts = filterQualifyingAccounts(allAccounts, targetDateStr);
            if (qualifyingAccounts.length > 0) {
                propertyCount++;
                itemCount += qualifyingAccounts.length;
            }
        });

        return {
            propertyCount,
            itemCount
        };
    }


    // Format date for display
    const formatSelectedDate = (dateKey) => {
        if (!dateKey || !dataToUse.data[dateKey]) {
            return { dayName: '', dayNumber: '', monthYear: '' };
        }

        const dayData = dataToUse.data[dateKey];
        // Handle date as string "2025-12-15" or ISO string
        const dateStr = typeof dayData.date === 'string' && dayData.date.includes('T')
            ? dayData.date.split('T')[0]
            : dayData.date;
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day, 12, 0, 0);

        return {
            dayName: format(date, 'EEEE'), // Full day name (Monday)
            dayNumber: format(date, 'd'), // Day number (1)
            monthYear: format(date, 'MMMM yyyy') // Month and year (December 2025)
        };
    }




    // Handle print button click - opens dialog
    const handlePrintClick = () => {
        setPrintDialogOpen(true);
    };

    // Execute print with filtered data
    const handleExecutePrint = (selectedGroups, selectedType) => {
        executePrint({
            dataToUse,
            selectedDate,
            selectedGroups,
            selectedType,
            getWeekStartDate,
            onCloseDialog: () => setPrintDialogOpen(false)
        });
    };

    const isLoading = accountDisconnects?.weeklyIsLoading || false;

    // Handlers for controlled accordion state
    const handleGroupAccordionChange = (groupUuid) => (event, isExpanded) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (isExpanded) {
                newSet.add(groupUuid);
            } else {
                newSet.delete(groupUuid);
            }
            return newSet;
        });
    };

    const handlePropertyAccordionChange = (propertyUuid) => (event, isExpanded) => {
        setExpandedProperties(prev => {
            const newSet = new Set(prev);
            if (isExpanded) {
                newSet.add(propertyUuid);
            } else {
                newSet.delete(propertyUuid);
            }
            return newSet;
        });
    };

    // Reusable component for rendering group sections (Remote/Non-Remote)
    const GroupSection = ({ groups, title, targetDateStr, sx = {} }) => {
        const groupsWithItems = groups.filter(g => getGroupStats(g, targetDateStr).itemCount > 0);
        return (
            <Box sx={{ marginBottom: 3, ...sx }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 2 }}>
                    {title}
                </Typography>
                {groupsWithItems.length === 0 ? (
                    <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        No disconnections scheduled
                    </Typography>
                ) : (
                    groupsWithItems.map((group) => {
                        const stats = getGroupStats(group, targetDateStr);
                        const isGroupExpanded = expandedGroups.has(group.groupUuid);
                        return (
                            <Accordion
                                key={group.groupUuid}
                                expanded={isGroupExpanded}
                                onChange={handleGroupAccordionChange(group.groupUuid)}
                                sx={{ marginBottom: 1 }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    onMouseDown={(e) => e.preventDefault()}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <Typography sx={{ fontWeight: 'bold', flex: 1 }}>
                                            {group.groupName}
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary', marginRight: 2 }}>
                                            {stats.propertyCount} {stats.propertyCount === 1 ? 'property' : 'properties'} • {stats.itemCount} {stats.itemCount === 1 ? 'item' : 'items'}
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {Object.keys(group.properties || {}).length === 0 ? (
                                        <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', padding: 2 }}>
                                            No disconnects for this group
                                        </Typography>
                                    ) : (
                                        Object.values(group.properties || {}).map((property) => {
                                            const allAccounts = getDisconnectsForDate(property, targetDateStr);
                                            const accounts = filterQualifyingAccounts(allAccounts, targetDateStr);
                                            if (accounts.length === 0) return null;
                                            const isPropertyExpanded = expandedProperties.has(property.propertyUuid);

                                            return (
                                                <Accordion
                                                    key={property.propertyUuid}
                                                    expanded={isPropertyExpanded}
                                                    onChange={handlePropertyAccordionChange(property.propertyUuid)}
                                                    sx={{
                                                        marginBottom: 1,
                                                        '&:before': {
                                                            display: 'none'
                                                        },
                                                        boxShadow: 'none',
                                                        border: '1px solid',
                                                        borderColor: 'divider'
                                                    }}
                                                >
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: 'action.hover'
                                                            },
                                                            paddingX: 2
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flex: 1 }}>
                                                                Property: {property.propertyName}
                                                            </Typography>
                                                            <Badge sx={{ fontSize: '12px', backgroundColor: '#f1f5fb', borderRadius: 5, padding: '2px 10px', fontWeight: 'bold', marginRight: 2 }}>
                                                                {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'}
                                                            </Badge>
                                                        </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails
                                                        sx={{ padding: 0 }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                    >
                                                        {accounts.length === 0 ? (
                                                            <Box sx={{ padding: 2 }}>
                                                                <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                                    No disconnects for this property
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{ padding: 2 }}>
                                                                <TableContainer
                                                                    component={Paper}
                                                                    variant="outlined"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell padding="checkbox" sx={{ fontWeight: 'bold' }}>
                                                                                    <Checkbox
                                                                                        indeterminate={selectedDisconnects.size > 0 && selectedDisconnects.size < accounts.length}
                                                                                        checked={accounts.length > 0 && selectedDisconnects.size === accounts.length}
                                                                                        onChange={(e) => handleSelectAll(e, accounts)}
                                                                                        color="primary"
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell sx={{ fontWeight: 'bold' }}>Account Holder</TableCell>
                                                                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                                                <TableCell sx={{ fontWeight: 'bold' }}>Property</TableCell>
                                                                                <TableCell sx={{ fontWeight: 'bold' }}>Address</TableCell>
                                                                                <TableCell sx={{ fontWeight: 'bold' }}>Meter Info</TableCell>
                                                                                <TableCell sx={{ fontWeight: 'bold' }}>Letter Sent Dates (Type)</TableCell>
                                                                                <TableCell sx={{ fontWeight: 'bold' }}>Disconnect Date</TableCell>
                                                                                <TableCell sx={{ fontWeight: 'bold' }}>Last Paid</TableCell>
                                                                                <TableCell sx={{ fontWeight: 'bold' }}>Disconnect Amt</TableCell>
                                                                                <TableCell sx={{ fontWeight: 'bold' }}>Current balance</TableCell>
                                                                                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {accounts.map((account, index) => {
                                                                                const accountKey = account.accountUuid || account.accountId || account.accountDisconnectId || `account-${index}`;
                                                                                const hasName = account.firstName || account.lastName;
                                                                                const accountDisplay = hasName
                                                                                    ? `${account.firstName || ''} ${account.lastName || ''}`.trim()
                                                                                    : (account.pmName || '');
                                                                                const accountNumber = account.accountNum || '';
                                                                                const disconnectUuid = account.disconnectUuid || account.accountDisconnectId || account.uuid || '';
                                                                                // Check if this disconnect was optimistically marked as pending
                                                                                const isOptimisticallyPending = pendingDisconnects.has(disconnectUuid);

                                                                                const addressParts = [];
                                                                                if (account.unit?.address1) addressParts.push(account.unit.address1);
                                                                                if (account.unit?.address2) addressParts.push(account.unit.address2);
                                                                                const address = addressParts.join(' ') || '';

                                                                                const disconnectDate = targetDateStr ? format(parseISO(targetDateStr + 'T00:00:00'), 'MM/dd/yyyy') : '';

                                                                                const lastPaid = account.lastPaymentDate
                                                                                    ? format(parseISO(account.lastPaymentDate), 'MM/dd/yyyy')
                                                                                    : 'N/A';

                                                                                const formattedAmt = new Intl.NumberFormat('en-US', {
                                                                                    style: 'currency',
                                                                                    currency: 'USD'
                                                                                }).format(account.disconnectAmount || 0);

                                                                                const formattedCurrentBalance =
                                                                                    account.accountBalance != null && account.accountBalance !== ''
                                                                                        ? new Intl.NumberFormat('en-US', {
                                                                                              style: 'currency',
                                                                                              currency: 'USD'
                                                                                          }).format(Number(account.accountBalance))
                                                                                        : 'N/A';

                                                                                const isSelected = selectedDisconnects.has(disconnectUuid);

                                                                                // Reconnect reason (matches batch Step 3 close rules)
                                                                                let reconnectReason = null;
                                                                                if (!account.completed) {
                                                                                    const disconnectAmt = Number(account.disconnectAmount) || 0;
                                                                                    const postCreationPledgeTotal = Number(account.postCreationPledgeTotal) || 0;
                                                                                    if (account.winterCrisis) {
                                                                                        reconnectReason = 'winter_crisis';
                                                                                    } else if (account.medForm) {
                                                                                        reconnectReason = 'med_form';
                                                                                    } else if (postCreationPledgeTotal >= disconnectAmt && disconnectAmt > 0) {
                                                                                        reconnectReason = 'pledge';
                                                                                    } else if (account.hasActivePaymentPlan) {
                                                                                        reconnectReason = 'payment_plan';
                                                                                    } else {
                                                                                        const paidSince = Number(account.totalPaidSinceDisconnect) || 0;
                                                                                        const pastDue = Number(account.pastDue) || 0;
                                                                                        const remainder = disconnectAmt - paidSince;
                                                                                        const paidOffDisconnect = disconnectAmt > 0 && paidSince > 0 && remainder <= 50;
                                                                                        const balanceBelowMin = pastDue < 50;
                                                                                        if (paidOffDisconnect || balanceBelowMin) {
                                                                                            reconnectReason = account.disconnected
                                                                                                ? 'paid_full_balance'
                                                                                                : 'paid_disconnect_amt';
                                                                                        }
                                                                                    }
                                                                                }
                                                                                const isReconnect = !!reconnectReason;

                                                                                const RECONNECT_LABELS = {
                                                                                    winter_crisis: 'Winter Crisis',
                                                                                    med_form: 'Med Form',
                                                                                    pledge: 'Pledge',
                                                                                    payment_plan: 'Payment Plan',
                                                                                };

                                                                                let statusChipLabel, statusColor, rowBgColor;
                                                                                if (isReconnect) {
                                                                                    statusColor = 'success';
                                                                                    rowBgColor = '#e8f5e9';
                                                                                    if (reconnectReason === 'paid_full_balance') {
                                                                                        statusChipLabel = 'Reconnect (paid full)';
                                                                                    } else if (reconnectReason === 'paid_disconnect_amt') {
                                                                                        statusChipLabel = 'Reconnect (paid disconnect amt)';
                                                                                    } else {
                                                                                        statusChipLabel = `Reconnect (${RECONNECT_LABELS[reconnectReason]})`;
                                                                                    }
                                                                                } else if (account.disconnected) {
                                                                                    statusChipLabel = 'Disconnected';
                                                                                    statusColor = 'error';
                                                                                    rowBgColor = '#f8d8e4';
                                                                                } else if (account.completed) {
                                                                                    statusChipLabel = 'Closed (Today)';
                                                                                    statusColor = 'success';
                                                                                    rowBgColor = '#e8f5e9';
                                                                                } else if (account.batchPending === 1 || isOptimisticallyPending) {
                                                                                    statusChipLabel = account.AccountStatus || 'Scheduled';
                                                                                    statusColor = 'primary';
                                                                                    rowBgColor = '#fffde7';
                                                                                } else {
                                                                                    statusChipLabel = account.AccountStatus || 'Scheduled';
                                                                                    statusColor = account.AccountStatus === 'Active' ? 'success' : 'default';
                                                                                    rowBgColor = isSelected ? 'action.selected' : 'transparent';
                                                                                }

                                                                                const rowHoverBgColor = isReconnect ? '#c8e6c9'
                                                                                    : account.disconnected ? '#f0b8c8'
                                                                                    : account.completed ? '#c8e6c9'
                                                                                    : (account.batchPending === 1 || isOptimisticallyPending) ? '#fff9c4'
                                                                                    : 'action.hover';

                                                                                const notices = account.notices || {};
                                                                                let letterSent = '';
                                                                                if (notices.mostRecentNotice) {
                                                                                    const noticeTypeMap = {
                                                                                        '14-day': '14',
                                                                                        '10-day': '10',
                                                                                        'final': 'F',
                                                                                        'disconnect': 'D'
                                                                                    };
                                                                                    const noticeType = noticeTypeMap[notices.mostRecentNotice] || '';
                                                                                    const noticeTypeToLetter = {
                                                                                        'disconnect': notices.letterDisconnect,
                                                                                        'final': notices.letterFinalNotice,
                                                                                        '10-day': notices.letter10Day,
                                                                                        '14-day': notices.letter14Day
                                                                                    };
                                                                                    const letterForNotice = noticeTypeToLetter[notices.mostRecentNotice];
                                                                                    const noticeDate = letterForNotice?.sendDate ||
                                                                                        notices.letter14Day?.sendDate ||
                                                                                        notices.letter10Day?.sendDate ||
                                                                                        notices.letterFinalNotice?.sendDate ||
                                                                                        notices.letterDisconnect?.sendDate || '';
                                                                                    if (noticeDate) {
                                                                                        const datePart = getScheduleDatePart(noticeDate);
                                                                                        const formattedDate = datePart ? format(parseISO(datePart + 'T00:00:00'), 'M/d/yyyy') : format(parseISO(noticeDate), 'M/d/yyyy');
                                                                                        letterSent = noticeType ? `(${noticeType}) ${formattedDate}` : formattedDate;
                                                                                    }
                                                                                }

                                                                                return (
                                                                                    <TableRow 
                                                                                        key={accountKey} 
                                                                                        selected={isSelected}
                                                                                        sx={{ 
                                                                                            backgroundColor: rowBgColor,
                                                                                            '&:hover': {
                                                                                                backgroundColor: rowHoverBgColor
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <TableCell padding="checkbox">
                                                                                            <Checkbox
                                                                                                checked={isSelected}
                                                                                                onChange={() => handleSelectDisconnect(disconnectUuid)}
                                                                                                disabled={isOptimisticallyPending || account.batchPending === 1}
                                                                                                color="primary"
                                                                                            />
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            <Box>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                                                    <Typography
                                                                                                        variant="body2"
                                                                                                        sx={{
                                                                                                            textDecoration: 'underline',
                                                                                                            cursor: 'pointer',
                                                                                                            color: disconnectUuid ? 'primary.main' : 'inherit',
                                                                                                            '&:hover': disconnectUuid ? { textDecoration: 'underline' } : {}
                                                                                                        }}
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            if (disconnectUuid) {
                                                                                                                window.open(`/disconnect/${disconnectUuid}`, '_blank', 'noopener,noreferrer');
                                                                                                            }
                                                                                                        }}
                                                                                                    >
                                                                                                        {accountDisplay}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                                                    {accountNumber}
                                                                                                </Typography>
                                                                                            </Box>
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            <Chip
                                                                                                label={statusChipLabel}
                                                                                                size="small"
                                                                                                color={statusColor}
                                                                                                sx={{
                                                                                                    backgroundColor: statusColor === 'error' ? '#d32f2f' :
                                                                                                        statusColor === 'success' ? '#4caf50' :
                                                                                                        statusColor === 'default' ? '#9e9e9e' : '#1976d2',
                                                                                                    color: 'white'
                                                                                                }}
                                                                                            />
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            <Typography variant="body2">
                                                                                                {property.propertyName || ''}
                                                                                            </Typography>
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            <Typography variant="body2">
                                                                                                {address}
                                                                                            </Typography>
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            <Typography variant="body2">
                                                                                                {account.meterNum || ''}
                                                                                            </Typography>
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            {letterSent || ''}
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            <Typography variant="body2">
                                                                                                {disconnectDate}
                                                                                            </Typography>
                                                                                        </TableCell>
                                                                                        <TableCell>{lastPaid}</TableCell>
                                                                                        <TableCell>{formattedAmt}</TableCell>
                                                                                        <TableCell>
                                                                                            <Typography variant="body2">{formattedCurrentBalance}</Typography>
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center', flexWrap: 'nowrap' }}>
                                                                                                <Tooltip title={getRunNowLabel(account)}>
                                                                                                    <IconButton
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            handleRunNow(account);
                                                                                                        }}
                                                                                                        size="small"
                                                                                                        disabled={isRunLoading || account.batchPending === 1 || account.effectiveHoldStatus === 1 || isOptimisticallyPending || account.disconnected || account.completed}
                                                                                                        sx={{
                                                                                                            '&:hover': {
                                                                                                                cursor: 'pointer',
                                                                                                                backgroundColor: 'action.hover'
                                                                                                            }
                                                                                                        }}
                                                                                                    >
                                                                                                        <PlayArrowOutlined />
                                                                                                    </IconButton>
                                                                                                </Tooltip>
                                <Tooltip title="Hold">
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleHold(account);
                                        }}
                                        size="small"
                                        disabled={isHoldLoading || account.effectiveHoldStatus === 1}
                                        sx={{
                                            '&:hover': {
                                                cursor: 'pointer',
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        <PauseOutlined />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Reschedule">
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleReschedule(account);
                                        }}
                                        size="small"
                                        disabled={isRescheduleLoading}
                                        sx={{
                                            '&:hover': {
                                                cursor: 'pointer',
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        <CalendarTodayOutlined />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Resend Notice">
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleResendNotice(account);
                                        }}
                                        size="small"
                                        disabled={isResendLoading}
                                        sx={{
                                            '&:hover': {
                                                cursor: 'pointer',
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        <EmailOutlined />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Revert Stage">
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRevert(account);
                                        }}
                                        size="small"
                                        disabled={isRevertLoading}
                                        sx={{
                                            '&:hover': {
                                                cursor: 'pointer',
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        <ReplayOutlined />
                                    </IconButton>
                                </Tooltip>
                                                                                            </Box>
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                )
                                                                            })}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            </Box>
                                                        )}
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        })
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })
                )}
            </Box>
        );
    };

    return (
        <>
            <style>{PRINT_STYLES}</style>
            <Box>
                <Box
                    className="no-print"
                    sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 1, marginBottom: 1, flexWrap: 'wrap' }}
                >
                    <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 600, px: 0.5 }}>
                        {format(getWeekStartDate(), 'MMM d')} – {format(displayedWeekEnd, 'MMM d, yyyy')}
                    </Typography>
                    {weekOffset !== 0 && (
                        <Button
                            className="no-print"
                            size="small"
                            variant="text"
                            onClick={() => setWeekOffset(0)}
                            disabled={isLoading}
                        >
                            This week
                        </Button>
                    )}
                </Box>

                {!selectedDate && (
                    <Box className="no-print" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, marginBottom: 2 }}>
                        <Button
                            className="no-print"
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={handleRefresh}
                            disabled={isLoading}
                        >
                            Refresh
                        </Button>
                        <Button
                            className="no-print"
                            variant="contained"
                            startIcon={<PrintIcon />}
                            onClick={handlePrintClick}
                            sx={{ marginLeft: 'auto' }}
                        >
                            Print Week
                        </Button>
                    </Box>
                )}

                {selectedDate && (() => {
                    const selectedDateData = getSelectedDateData();
                    if (!selectedDateData) return null;

                    const { remote, nonRemote } = organizeGroupsByRemoteStatus(selectedDateData);
                    const dateDisplay = formatSelectedDate(selectedDate);
                    // Handle date as string "2025-12-15" or ISO string
                    const targetDateStr = typeof selectedDateData.date === 'string' && selectedDateData.date.includes('T')
                        ? selectedDateData.date.split('T')[0]
                        : selectedDateData.date;

                    return (
                        <Box id="full-details" sx={{
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300',
                            width: '100%',
                            padding: 3,
                            backgroundColor: 'grey.200',
                            marginTop: 2,
                            marginBottom: 2,
                            overflowAnchor: 'none'
                        }}>
                            {/* Header with Date and Print Button */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 3
                            }}>
                                <Box sx={{ textAlign: 'center', flex: 1 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                                        {dateDisplay.dayName}
                                    </Typography>
                                    <Typography variant="h2" sx={{ fontWeight: 'bold', fontSize: '2rem', marginBottom: 1 }}>
                                        {dateDisplay.dayNumber}
                                    </Typography>
                                    <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                                        {dateDisplay.monthYear}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Button
                                        className="no-print"
                                        variant="outlined"
                                        startIcon={<Refresh />}
                                        onClick={handleRefresh}
                                        disabled={isLoading}
                                    >
                                        Refresh
                                    </Button>
                                    <Button
                                        className="no-print"
                                        variant="contained"
                                        startIcon={<PrintIcon />}
                                        onClick={handlePrintClick}
                                    >
                                        Print Day
                                    </Button>
                                </Box>
                            </Box>

                            {/* Remote Groups Section */}
                            <GroupSection
                                groups={remote}
                                title="Remote Groups"
                                targetDateStr={targetDateStr}
                            />

                            {/* Non-Remote Groups Section */}
                            <GroupSection
                                groups={nonRemote}
                                title="Non-Remote Groups"
                                targetDateStr={targetDateStr}
                                sx={{ marginBottom: 0 }}
                            />
                        </Box>
                    );
                })()}
                <Box
                    className="no-print"
                    sx={{
                        position: 'relative',
                        paddingY: '20px',
                        pl: weekOffset > 0 ? { xs: 7, sm: 8 } : 0,
                        pr: weekOffset === 0 ? { xs: 7, sm: 8 } : 0,
                    }}
                >
                    {weekOffset > 0 && (
                        <Fab
                            className="no-print"
                            size="small"
                            color="primary"
                            aria-label="Previous week"
                            onClick={() => setWeekOffset((o) => o - 1)}
                            disabled={isLoading}
                            sx={{
                                position: 'absolute',
                                left: { xs: 4, sm: 8 },
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 2,
                                boxShadow: 'none',
                                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                '&:hover:not(:disabled)': {
                                    transform: 'translateY(calc(-50% - 2px))',
                                    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.15)',
                                },
                            }}
                        >
                            <ChevronLeft />
                        </Fab>
                    )}
                    {weekOffset === 0 && (
                        <Fab
                            className="no-print"
                            size="small"
                            color="primary"
                            aria-label="Next week"
                            onClick={() => setWeekOffset((o) => o + 1)}
                            disabled={isLoading}
                            sx={{
                                position: 'absolute',
                                right: { xs: 4, sm: 8 },
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 2,
                                boxShadow: 'none',
                                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                '&:hover:not(:disabled)': {
                                    transform: 'translateY(calc(-50% - 2px))',
                                    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.15)',
                                },
                            }}
                        >
                            <ChevronRight />
                        </Fab>
                    )}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '10px',
                        alignContent: 'center',
                        justifyContent: 'space-around',
                        overflowX: { xs: 'scroll', sm: 'scroll', md: 'hidden' },
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        }
                    }}>

                    {Object.entries(dataToUse.data).map(([key, dayData]) => {
                        // Extract date parts - handle both date string "2025-12-15" and ISO string
                        const dateStr = typeof dayData.date === 'string' && dayData.date.includes('T')
                            ? dayData.date.split('T')[0]
                            : dayData.date; // Get "2025-12-01"
                        const [year, month, day] = dateStr.split('-').map(Number);
                        const date = new Date(year, month - 1, day, 12, 0, 0); // month is 0-indexed
                        // Use the actual date to get day abbreviation, not the key (which may have timezone issues)
                        const dayAbbreviation = format(date, 'EEE'); // e.g., "Mon", "Tue"
                        const dayNumber = format(date, 'd');
                        const monthAbbr = format(date, 'MMM');

                        // Get remote and non-remote groups for this day
                        const { remote, nonRemote } = organizeGroupsByRemoteStatus(dayData);

                        // Check if this day is a holiday or weekend
                        const holidayName = isHoliday(date);
                        const isWeekendDay = isWeekend(date);

                        return (
                            <Card
                                key={dayData.date}
                                ref={(el) => { cardRefs.current[key] = el; }}
                                onClick={() => (key == selectedDate ? handleSetSelectedDate(null) : handleSetSelectedDate(key))}
                                sx={{
                                    height: '50%',
                                    width: '50%',
                                    minWidth: '120px',
                                    minHeight: '240px',
                                    border: '1px solid',
                                    borderColor: 'grey.300',
                                    borderRadius: 1,
                                    backgroundColor: key === selectedDate ? 'grey.200' : 'white',
                                    boxShadow: key === selectedDate ? '0 0 10px 0 rgba(0, 0, 0, 0.1)' : 'none',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.15)',
                                    }
                                }}>
                                {/* Holiday/Weekend Indicator Icon */}
                                {(holidayName || isWeekendDay) && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            zIndex: 1
                                        }}
                                    >
                                        {holidayName ? (
                                            <Tooltip title={holidayName}>
                                                <EventOutlined
                                                    sx={{
                                                        fontSize: '20px',
                                                        color: 'text.secondary',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Weekend">
                                                <WeekendOutlined
                                                    sx={{
                                                        fontSize: '20px',
                                                        color: 'text.secondary',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontWeight: 500 }}>

                                    <p style={{ marginTop: '20px', marginBottom: 0, fontSize: '18px' }}>{dayAbbreviation}</p>
                                    <p style={{ margin: 0, fontSize: '32px' }}>{dayNumber}</p>
                                    <p style={{ margin: 0, color: 'gray' }}>{monthAbbr}</p>
                                    <Badge sx={{ fontSize: '12px', backgroundColor: '#f1f5fb', borderRadius: 5, padding: '2px 10px', fontWeight: 'bold' }}>
                                        {isLoading ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CircularProgress size={12} thickness={4} />
                                                <span>Loading...</span>
                                            </Box>
                                        ) : (
                                            `${getDisconnectCountForDate(dayData)} ${getDisconnectCountForDate(dayData) === 1 ? 'item' : 'items'}`
                                        )}
                                    </Badge>
                                    <Box sx={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: 'gray',
                                        textAlign: 'left',
                                        marginY: '12px',
                                        alignSelf: 'stretch',
                                        width: '100%',
                                        paddingLeft: '16px',
                                        paddingRight: '8px',
                                        boxSizing: 'border-box'
                                    }}>
                                        <p style={{ marginBottom: '4px' }}>Remote</p>
                                        {remote.length === 0 ? (
                                            <p style={{ margin: 0, fontStyle: 'italic', marginLeft: '8px' }}>None</p>
                                        ) : (
                                            remote.map((group) => {
                                                const propertyCount = Object.keys(group.properties || {}).length;
                                                return (
                                                    <Badge
                                                        key={group.groupUuid}
                                                        sx={{
                                                            fontSize: '12px',
                                                            backgroundColor: '#f1f5fb',
                                                            borderRadius: 5,
                                                            color: 'black',
                                                            fontWeight: '400',
                                                            padding: '2px 10px',
                                                            display: 'block',
                                                            marginBottom: '4px',
                                                            marginLeft: '8px',
                                                            width: 'fit-content'
                                                        }}
                                                    >
                                                        {group.groupName} ({propertyCount} {propertyCount === 1 ? 'prop' : 'props'})
                                                    </Badge>
                                                );
                                            })
                                        )}
                                        <p style={{ marginTop: '8px', marginBottom: '4px' }}>Non-Remote</p>
                                        {nonRemote.length === 0 ? (
                                            <p style={{ margin: 0, fontStyle: 'italic', marginLeft: '8px' }}>None</p>
                                        ) : (
                                            nonRemote.map((group) => {
                                                const propertyCount = Object.keys(group.properties || {}).length;
                                                return (
                                                    <Badge
                                                        key={group.groupUuid}
                                                        sx={{
                                                            fontSize: '12px',
                                                            backgroundColor: '#f1f5fb',
                                                            color: 'black',
                                                            fontWeight: '400',
                                                            borderRadius: 5,
                                                            padding: '2px 10px',
                                                            display: 'block',
                                                            marginBottom: '4px',
                                                            marginLeft: '8px',
                                                            width: 'fit-content'
                                                        }}
                                                    >
                                                        {group.groupName} ({propertyCount} {propertyCount === 1 ? 'prop' : 'props'})
                                                    </Badge>
                                                );
                                            })
                                        )}
                                    </Box>

                                </Box>
                            </Card>
                        );
                    })}
                    </Box>
                </Box>

                {/* Print Dialog */}
                <PrintDialog
                    open={printDialogOpen}
                    onClose={() => setPrintDialogOpen(false)}
                    onPrint={handleExecutePrint}
                    availableGroups={getAvailableGroupsForPrint(dataToUse, selectedDate)}
                    printScope={selectedDate ? 'day' : 'week'}
                    getFilteredGroupCount={(selectedGroups, selectedType) =>
                        getFilteredGroupCount(dataToUse, selectedDate, selectedGroups, selectedType)
                    }
                />

                {/* Hold Dialog */}
                <Dialog open={holdModalOpen} onClose={() => {
                    setHoldModalOpen(false);
                    setHoldNote("");
                    setSelectedAccount(null);
                    setIsHoldLoading(false);
                }} maxWidth="sm" fullWidth>
                    <DialogTitle>Hold Disconnection</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Please provide a reason for holding this disconnection.
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Hold Reason"
                            value={holdNote}
                            onChange={(e) => setHoldNote(e.target.value)}
                            placeholder="Enter reason for holding this disconnection..."
                            sx={{ mt: 2 }}
                            required
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setHoldModalOpen(false);
                            setHoldNote("");
                            setSelectedAccount(null);
                            setIsHoldLoading(false);
                        }}>Cancel</Button>
                        <Button
                            onClick={handleHoldSubmit}
                            variant="contained"
                            color="primary"
                            disabled={!holdNote.trim() || isHoldLoading}
                        >
                            {isHoldLoading ? "Holding..." : "Hold"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Reschedule Dialog */}
                <Dialog open={rescheduleModalOpen} onClose={() => {
                    setRescheduleModalOpen(false);
                    setSelectedAccount(null);
                    setIsRescheduleLoading(false);
                }} maxWidth="sm" fullWidth>
                    <DialogTitle>Reschedule Disconnection</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Are you sure you want to reschedule this disconnect? This will update the manual hold date to the current date.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setRescheduleModalOpen(false);
                            setSelectedAccount(null);
                            setIsRescheduleLoading(false);
                        }}>Cancel</Button>
                        <Button
                            onClick={handleRescheduleSubmit}
                            variant="contained"
                            color="primary"
                            disabled={isRescheduleLoading}
                        >
                            {isRescheduleLoading ? "Rescheduling..." : "Reschedule"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Revert Dialog */}
                <Dialog open={revertModalOpen} onClose={() => {
                    setRevertModalOpen(false);
                    setSelectedAccount(null);
                    setIsRevertLoading(false);
                }} maxWidth="sm" fullWidth>
                    <DialogTitle>Revert Disconnection</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Are you sure you want to revert this disconnection to the previous stage?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setRevertModalOpen(false);
                            setSelectedAccount(null);
                            setIsRevertLoading(false);
                        }}>Cancel</Button>
                        <Button
                            onClick={handleRevertSubmit}
                            variant="contained"
                            color="primary"
                            disabled={isRevertLoading}
                        >
                            {isRevertLoading ? "Reverting..." : "Revert"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Floating Action Buttons */}
                {selectedDisconnects.size > 0 && (
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: 24,
                            right: 24,
                            zIndex: 1000,
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            maxWidth: 'calc(100vw - 48px)',
                        }}
                    >
                        {/* Clear Selection Button */}
                        <IconButton
                            onClick={() => setSelectedDisconnects(new Set())}
                            sx={{
                                width: 48,
                                height: 48,
                                backgroundColor: 'grey.600',
                                color: 'white',
                                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
                                '&:hover': {
                                    backgroundColor: 'grey.700',
                                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.2)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            <ClearOutlined />
                        </IconButton>

                        {/* Hold Selected Button */}
                        <Button
                            variant="contained"
                            color="warning"
                            size="medium"
                            startIcon={<PauseOutlined />}
                            onClick={handleHoldMulti}
                            sx={{
                                minWidth: 160,
                                height: 48,
                                borderRadius: 2,
                                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                '&:hover': {
                                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.2)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            Hold ({selectedDisconnects.size})
                        </Button>

                        {/* Run Now Selected Button */}
                        <Button
                            variant="contained"
                            color="success"
                            size="medium"
                            startIcon={<PlayArrowOutlined />}
                            onClick={handleRunNowMulti}
                            sx={{
                                minWidth: 160,
                                height: 48,
                                borderRadius: 2,
                                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                '&:hover': {
                                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.2)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            Run Now ({selectedDisconnects.size})
                        </Button>

                        {/* Reschedule Selected Button */}
                        <Button
                            variant="contained"
                            color="info"
                            size="medium"
                            startIcon={<CalendarTodayOutlined />}
                            onClick={handleRescheduleMulti}
                            sx={{
                                minWidth: 160,
                                height: 48,
                                borderRadius: 2,
                                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                '&:hover': {
                                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.2)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            Reschedule ({selectedDisconnects.size})
                        </Button>

                        {/* Resend Notice Selected Button */}
                        <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            startIcon={<EmailOutlined />}
                            onClick={handleResendNoticeMulti}
                            sx={{
                                minWidth: 160,
                                height: 48,
                                borderRadius: 2,
                                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                '&:hover': {
                                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.2)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            Resend Notice ({selectedDisconnects.size})
                        </Button>

                        {/* Revert Selected Button */}
                        <Button
                            variant="contained"
                            color="error"
                            size="medium"
                            startIcon={<ReplayOutlined />}
                            onClick={handleRevertMulti}
                            sx={{
                                minWidth: 160,
                                height: 48,
                                borderRadius: 2,
                                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                '&:hover': {
                                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.2)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            Revert ({selectedDisconnects.size})
                        </Button>
                    </Box>
                )}

                {/* Bulk Hold Dialog */}
                <Dialog
                    open={bulkHoldDialogOpen}
                    onClose={() => setBulkHoldDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Hold Selected Disconnects</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Please provide a reason for holding {selectedDisconnects.size} selected disconnection{selectedDisconnects.size !== 1 ? 's' : ''}.
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Hold Reason"
                            value={holdNote}
                            onChange={(e) => setHoldNote(e.target.value)}
                            placeholder="Enter reason for holding these disconnections..."
                            sx={{ mt: 2 }}
                            required
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setBulkHoldDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={async () => {
                                if (!holdNote.trim()) return;
                                try {
                                    const selectedArray = Array.from(selectedDisconnects);
                                    const allAccounts = getAllAccountsFromView();
                                    
                                    const uuidToNameMap = new Map();
                                    const uuidToIdMap = new Map();
                                    const disconnectHolds = [];
                                    
                                    selectedArray.forEach((uuid) => {
                                        const account = allAccounts.find((a) => (a.disconnectUuid || a.accountDisconnectId || a.uuid) === uuid);
                                        if (account) {
                                            const name = getFullName(account);
                                            const disconnectId = account.disconnectId || account.accountDisconnectId || account.id || 'N/A';
                                            const accountId = account.accountId;
                                            
                                            uuidToNameMap.set(uuid, name);
                                            uuidToIdMap.set(uuid, disconnectId);
                                            
                                            if (accountId) {
                                                const expiresAtDate = new Date();
                                                expiresAtDate.setDate(expiresAtDate.getDate() + 30);
                                                const expiresAt = expiresAtDate.toISOString().slice(0, 19).replace('T', ' ');
                                                
                                                disconnectHolds.push({
                                                    holdType: 'ACCOUNT',
                                                    accountId: accountId,
                                                    disconnectUuid: uuid,
                                                    reason: holdNote.trim(),
                                                    notes: holdNote.trim(),
                                                    expiresAt: expiresAt,
                                                    isActive: 1,
                                                    liftReason: '',
                                                });
                                            }
                                        }
                                    });
                                    
                                    setDisconnectUuidToName(uuidToNameMap);
                                    setDisconnectUuidToId(uuidToIdMap);
                                    
                                    const response = await createDisconnectHoldsBulk(disconnectHolds);
                                    
                                    setBulkActionResults(response);
                                    setBulkActionType('hold');
                                    setBulkHoldDialogOpen(false);
                                    setHoldNote("");
                                    setSelectedDisconnects(new Set());
                                    setBulkActionResultsOpen(true);
                                    
                                    refreshData();
                                } catch (error) {
                                    console.error("Error holding disconnects:", error);
                                    const errorMessage = error?.response?.data?.ERROR ||
                                        error?.response?.data?.message ||
                                        error?.message ||
                                        "Failed to hold disconnections";
                                    dispatch(setSnackbar(true, "error", errorMessage));
                                    setBulkHoldDialogOpen(false);
                                }
                            }}
                            variant="contained"
                            color="primary"
                            disabled={!holdNote.trim()}
                        >
                            Hold
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Bulk Run Now Dialog */}
                <Dialog
                    open={bulkRunNowDialogOpen}
                    onClose={() => setBulkRunNowDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Run Now Selected Disconnects</DialogTitle>
                    <DialogContent>
                        {(() => {
                            const selectedArray = Array.from(selectedDisconnects);
                            const allAccounts = getAllAccountsFromView();
                            let finalCount = 0;
                            let disconnectCount = 0;
                            selectedArray.forEach((uuid) => {
                                const account = allAccounts.find((a) => (a.disconnectUuid || a.accountDisconnectId || a.uuid) === uuid);
                                if (account) {
                                    const stage = getCurrentStage(account);
                                    if (stage === "14-day" || stage === "10-day") {
                                        finalCount++;
                                    } else {
                                        disconnectCount++;
                                    }
                                }
                            });
                            return (
                                <>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Are you sure you want to process {selectedDisconnects.size} selected disconnect{selectedDisconnects.size !== 1 ? 's' : ''}?
                                    </Typography>
                                    {finalCount > 0 && (
                                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'warning.main' }}>
                                            {finalCount} account{finalCount !== 1 ? 's' : ''} on 14/10-day notice → will generate Final Notice
                                        </Typography>
                                    )}
                                    {disconnectCount > 0 && (
                                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'error.main' }}>
                                            {disconnectCount} account{disconnectCount !== 1 ? 's' : ''} on Final Notice → will process Disconnect
                                        </Typography>
                                    )}
                                </>
                            );
                        })()}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setBulkRunNowDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={async () => {
                                try {
                                    const selectedArray = Array.from(selectedDisconnects);
                                    const allAccounts = getAllAccountsFromView();
                                    
                                    const uuidToNameMap = new Map();
                                    const uuidToIdMap = new Map();
                                    const finalReadyUuids = [];
                                    const disconnectUuids = [];

                                    selectedArray.forEach((uuid) => {
                                        const account = allAccounts.find((a) => (a.disconnectUuid || a.accountDisconnectId || a.uuid) === uuid);
                                        if (account) {
                                            const name = getFullName(account);
                                            const disconnectId = account.disconnectId || account.accountDisconnectId || account.id || 'N/A';
                                            uuidToNameMap.set(uuid, name);
                                            uuidToIdMap.set(uuid, disconnectId);

                                            const stage = getCurrentStage(account);
                                            if (stage === "14-day" || stage === "10-day") {
                                                finalReadyUuids.push(uuid);
                                            } else {
                                                disconnectUuids.push(uuid);
                                            }
                                        }
                                    });
                                    
                                    setDisconnectUuidToName(uuidToNameMap);
                                    setDisconnectUuidToId(uuidToIdMap);
                                    
                                    const promises = [];
                                    if (finalReadyUuids.length > 0) {
                                        promises.push(markDisconnectsAsPendingBulk(finalReadyUuids, false, 'final_ready'));
                                    }
                                    if (disconnectUuids.length > 0) {
                                        promises.push(markDisconnectsAsPendingBulk(disconnectUuids, false, ''));
                                    }
                                    
                                    await Promise.all(promises);
                                    
                                    setBulkActionType('runNow');
                                    setBulkRunNowDialogOpen(false);
                                    setSelectedDisconnects(new Set());
                                    
                                    dispatch(setSnackbar(true, "success", `Run Now processed: ${finalReadyUuids.length} Final Notice(s), ${disconnectUuids.length} Disconnect(s)`));
                                    
                                    refreshData();
                                } catch (error) {
                                    console.error("Error marking disconnects as pending:", error);
                                    const errorMessage = error?.response?.data?.ERROR ||
                                        error?.response?.data?.message ||
                                        error?.message ||
                                        "Failed to mark disconnects as pending";
                                    dispatch(setSnackbar(true, "error", errorMessage));
                                    setBulkRunNowDialogOpen(false);
                                }
                            }}
                            variant="contained"
                            color="success"
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Bulk Reschedule Dialog */}
                <Dialog
                    open={bulkRescheduleDialogOpen}
                    onClose={() => setBulkRescheduleDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Reschedule Selected Disconnects</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Are you sure you want to reschedule {selectedDisconnects.size} selected disconnect{selectedDisconnects.size !== 1 ? 's' : ''}? This will update the manual hold date to the current date.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setBulkRescheduleDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={async () => {
                                try {
                                    const selectedArray = Array.from(selectedDisconnects);
                                    const allAccounts = getAllAccountsFromView();
                                    
                                    const uuidToNameMap = new Map();
                                    const uuidToIdMap = new Map();
                                    selectedArray.forEach((uuid) => {
                                        const account = allAccounts.find((a) => (a.disconnectUuid || a.accountDisconnectId || a.uuid) === uuid);
                                        if (account) {
                                            const name = getFullName(account);
                                            const disconnectId = account.disconnectId || account.accountDisconnectId || 'N/A';
                                            uuidToNameMap.set(uuid, name);
                                            uuidToIdMap.set(uuid, disconnectId);
                                        }
                                    });
                                    
                                    setDisconnectUuidToName(uuidToNameMap);
                                    setDisconnectUuidToId(uuidToIdMap);
                                    
                                    const response = await rescheduleAccountDisconnectsBulk(selectedArray);
                                    
                                    setBulkActionResults(response);
                                    setBulkActionType('reschedule');
                                    setBulkRescheduleDialogOpen(false);
                                    setSelectedDisconnects(new Set());
                                    setBulkActionResultsOpen(true);
                                    
                                    refreshData();
                                } catch (error) {
                                    console.error("Error rescheduling disconnects:", error);
                                    const errorMessage = error?.response?.data?.ERROR ||
                                        error?.response?.data?.message ||
                                        error?.message ||
                                        "Failed to reschedule disconnections";
                                    dispatch(setSnackbar(true, "error", errorMessage));
                                    setBulkRescheduleDialogOpen(false);
                                }
                            }}
                            variant="contained"
                            color="primary"
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Bulk Resend Notice Dialog */}
                <Dialog
                    open={bulkResendNoticeDialogOpen}
                    onClose={() => setBulkResendNoticeDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Resend Notice Selected Disconnects</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Are you sure you want to resend notices for {selectedDisconnects.size} selected disconnect{selectedDisconnects.size !== 1 ? 's' : ''}?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setBulkResendNoticeDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={async () => {
                                try {
                                    const selectedArray = Array.from(selectedDisconnects);
                                    const allAccounts = getAllAccountsFromView();
                                    
                                    const uuidToNameMap = new Map();
                                    const uuidToIdMap = new Map();
                                    selectedArray.forEach((uuid) => {
                                        const account = allAccounts.find((a) => (a.disconnectUuid || a.accountDisconnectId || a.uuid) === uuid);
                                        if (account) {
                                            const name = getFullName(account);
                                            const disconnectId = account.disconnectId || account.accountDisconnectId || 'N/A';
                                            uuidToNameMap.set(uuid, name);
                                            uuidToIdMap.set(uuid, disconnectId);
                                        }
                                    });
                                    
                                    setDisconnectUuidToName(uuidToNameMap);
                                    setDisconnectUuidToId(uuidToIdMap);
                                    
                                    const response = await resendDisconnectNoticesBulk(selectedArray);
                                    
                                    setBulkActionResults(response);
                                    setBulkActionType('resendNotice');
                                    setBulkResendNoticeDialogOpen(false);
                                    setSelectedDisconnects(new Set());
                                    setBulkActionResultsOpen(true);
                                    
                                    refreshData();
                                } catch (error) {
                                    console.error("Error resending notices:", error);
                                    const errorMessage = error?.response?.data?.ERROR ||
                                        error?.response?.data?.message ||
                                        error?.message ||
                                        "Failed to resend notices";
                                    dispatch(setSnackbar(true, "error", errorMessage));
                                    setBulkResendNoticeDialogOpen(false);
                                }
                            }}
                            variant="contained"
                            color="primary"
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Bulk Revert Dialog */}
                <Dialog
                    open={bulkRevertDialogOpen}
                    onClose={() => setBulkRevertDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Revert Selected Disconnects</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            This will revert {selectedDisconnects.size} selected disconnect{selectedDisconnects.size !== 1 ? 's' : ''} to their previous stage.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setBulkRevertDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={async () => {
                                try {
                                    const selectedArray = Array.from(selectedDisconnects);
                                    const allAccounts = getAllAccountsFromView();
                                    
                                    const uuidToNameMap = new Map();
                                    const uuidToIdMap = new Map();
                                    selectedArray.forEach((uuid) => {
                                        const account = allAccounts.find((a) => (a.disconnectUuid || a.accountDisconnectId || a.uuid) === uuid);
                                        if (account) {
                                            const name = getFullName(account);
                                            const disconnectId = account.disconnectId || account.accountDisconnectId || 'N/A';
                                            uuidToNameMap.set(uuid, name);
                                            uuidToIdMap.set(uuid, disconnectId);
                                        }
                                    });
                                    
                                    setDisconnectUuidToName(uuidToNameMap);
                                    setDisconnectUuidToId(uuidToIdMap);
                                    
                                    const response = await revertAccountDisconnectsBulk(selectedArray);
                                    
                                    setBulkActionResults(response);
                                    setBulkActionType('revert');
                                    setBulkRevertDialogOpen(false);
                                    setSelectedDisconnects(new Set());
                                    setBulkActionResultsOpen(true);
                                    
                                    refreshData();
                                } catch (error) {
                                    console.error("Error reverting disconnects:", error);
                                    const errorMessage = error?.response?.data?.ERROR ||
                                        error?.response?.data?.message ||
                                        error?.message ||
                                        "Failed to revert disconnects";
                                    dispatch(setSnackbar(true, "error", errorMessage));
                                    setBulkRevertDialogOpen(false);
                                }
                            }}
                            variant="contained"
                            sx={{ bgcolor: "red" }}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Bulk Action Results Modal */}
                <Dialog
                    open={bulkActionResultsOpen}
                    onClose={() => setBulkActionResultsOpen(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        Bulk {bulkActionType === 'hold' ? 'Hold' : bulkActionType === 'runNow' ? 'Run Now' : bulkActionType === 'reschedule' ? 'Reschedule' : bulkActionType === 'resendNotice' ? 'Resend Notice' : bulkActionType === 'revert' ? 'Revert' : 'Action'} Results
                    </DialogTitle>
                    <DialogContent>
                        {bulkActionResults && (() => {
                            const results = bulkActionResults.results || [];
                            const errors = bulkActionResults.errors || [];
                            const successCount = results.length;
                            const errorCount = errors.length;
                            const totalCount = successCount + errorCount;

                            return (
                                <Box sx={{ mt: 1 }}>
                                    {/* Total Counts Summary */}
                                    <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="h6" gutterBottom>
                                            Summary
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    Total Processed
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                    {totalCount}
                                                </Typography>
                                            </Box>
                                            {successCount > 0 && (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Successful
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                        {successCount}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {errorCount > 0 && (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Unsuccessful
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                                        {errorCount}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Successful Results Box */}
                                    {successCount > 0 && (
                                        <Box sx={{ mb: 3 }}>
                                            <Box sx={{ 
                                                p: 2, 
                                                backgroundColor: 'success.light', 
                                                borderRadius: 1, 
                                                border: '2px solid', 
                                                borderColor: 'success.main',
                                                mb: 2
                                            }}>
                                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                                                    ✓ Successful ({successCount})
                                                </Typography>
                                            </Box>
                                            <TableContainer component={Paper} sx={{ maxHeight: 300, border: '1px solid', borderColor: 'success.main' }}>
                                                <Table stickyHeader size="small">
                                                    <TableHead>
                                                        <TableRow sx={{ backgroundColor: 'success.light' }}>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Account Holder</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Disconnect ID</TableCell>
                                                            {bulkActionType === 'revert' && (
                                                                <>
                                                                    <TableCell sx={{ fontWeight: 'bold' }}>Previous Stage</TableCell>
                                                                    <TableCell sx={{ fontWeight: 'bold' }}>New Stage</TableCell>
                                                                </>
                                                            )}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {results.map((result, index) => {
                                                            const uuid = result.disconnectUuid || result.uuid;
                                                            const name = disconnectUuidToName.get(uuid) || 'N/A';
                                                            const disconnectId = result.disconnectId || disconnectUuidToId.get(uuid) || 'N/A';
                                                            return (
                                                                <TableRow key={index}>
                                                                    <TableCell>
                                                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                            {name}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>{disconnectId}</TableCell>
                                                                    {bulkActionType === 'revert' && (
                                                                        <>
                                                                            <TableCell>{result.previousStage}</TableCell>
                                                                            <TableCell>{result.newStage}</TableCell>
                                                                        </>
                                                                    )}
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}

                                    {/* Unsuccessful Results Box */}
                                    {errorCount > 0 && (
                                        <Box>
                                            <Box sx={{ 
                                                p: 2, 
                                                backgroundColor: 'error.light', 
                                                borderRadius: 1, 
                                                border: '2px solid', 
                                                borderColor: 'error.main',
                                                mb: 2
                                            }}>
                                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                                                    ✗ Unsuccessful ({errorCount})
                                                </Typography>
                                            </Box>
                                            <TableContainer component={Paper} sx={{ maxHeight: 300, border: '1px solid', borderColor: 'error.main' }}>
                                                <Table stickyHeader size="small">
                                                    <TableHead>
                                                        <TableRow sx={{ backgroundColor: 'error.light' }}>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Account Holder</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Disconnect ID</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Error</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {errors.map((error, index) => {
                                                            const uuid = error.disconnectUuid || error.uuid;
                                                            const name = disconnectUuidToName.get(uuid) || 'N/A';
                                                            const disconnectId = error.disconnectId || disconnectUuidToId.get(uuid) || 'N/A';
                                                            return (
                                                                <TableRow key={index}>
                                                                    <TableCell>
                                                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                            {name}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>{disconnectId}</TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2" color="error">
                                                                            {error.error || error.message || 'Unknown error'}
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })()}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setBulkActionResultsOpen(false)} variant="contained">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box >
        </>
    )
}

WeekView.propTypes = {
    Data: PropTypes.object,
    accountDisconnects: PropTypes.object.isRequired,
    getWeekly: PropTypes.func.isRequired,
    markDisconnectAsPending: PropTypes.func.isRequired,
    markDisconnectsAsPendingBulk: PropTypes.func.isRequired,
    resendDisconnectNotice: PropTypes.func.isRequired,
    resendDisconnectNoticesBulk: PropTypes.func.isRequired,
    revertAccountDisconnect: PropTypes.func.isRequired,
    revertAccountDisconnectsBulk: PropTypes.func.isRequired,
    rescheduleAccountDisconnect: PropTypes.func.isRequired,
    rescheduleAccountDisconnectsBulk: PropTypes.func.isRequired,
    createDisconnectHold: PropTypes.func.isRequired,
    createDisconnectHoldsBulk: PropTypes.func.isRequired,
};

function WeekViewState(state) {
    return {
        accountDisconnects: state.accountDisconnects,
    };
}

export default connect(WeekViewState, {
    getWeekly,
    markDisconnectAsPending,
    markDisconnectsAsPendingBulk,
    resendDisconnectNotice,
    resendDisconnectNoticesBulk,
    revertAccountDisconnect,
    revertAccountDisconnectsBulk,
    rescheduleAccountDisconnect,
    rescheduleAccountDisconnectsBulk,
    createDisconnectHold,
    createDisconnectHoldsBulk
})(WeekView);